import type { Translations } from "./types"

export const pl: Translations = {
	appName: "zPlanem",

	hero: {
		sentences: [
			"Od planu, po wykonanie",
			"Bez stresu",
			"Cały czas na bieżąco",
			"AI wsparcie na każdym kroku",
		],
	},

	language: {
		label: "Język",
		pl: "Polski",
		en: "English",
		uk: "Українська",
	},

	common: {
		loading: "Ładowanie...",
	},

	auth: {
		login: "Zaloguj się",
		logout: "Wyloguj się",
		googleLogin: "Kontynuuj z Google",
		getStarted: "Zacznij tutaj",
		backToMain: "Powrót do strony głównej",
	},

	dashboard: {
		title: "Panel nawigacyjny",
		welcome: "Witaj z powrotem!",
		dummyText: "Tutaj będą Twoje nieruchomości i postępy prac.",
	},

	properties: {
		newPropertyCta: "+ Nowa nieruchomość",
		addFirstPropertyCta: "Dodaj swoją pierwszą nieruchomość",
		emptyStateTitle: "Brak nieruchomości",
		emptyStateBody: "Dodaj pierwszą nieruchomość, aby zacząć planować remont.",
		lastOpenedLabel: "Ostatnio otwierana",
		propertiesListHeading: "Twoje nieruchomości",
		types: {
			apartment: "Mieszkanie",
			house: "Dom",
			office: "Biuro",
			other: "Inny",
		},
		tabs: {
			navLabel: "Sekcje nieruchomości",
			overview: "Przegląd",
			projects: "Projekty",
			tickets: "Zadania",
			journal: "Dziennik",
			documents: "Dokumenty",
			budget: "Budżet",
		},
		form: {
			newPageTitle: "Nowa nieruchomość",
			nameLabel: "Nazwa",
			namePlaceholder: "np. Mokotowska 12",
			typeLabel: "Typ",
			customTypeLabel: "Opisz typ",
			customTypePlaceholder: "np. garaż, domek letniskowy",
			descriptionLabel: "Opis",
			descriptionPlaceholder: "Opcjonalne notatki o nieruchomości",
			submit: "Dodaj nieruchomość",
			cancel: "Anuluj",
			errors: {
				nameRequired: "Nazwa jest wymagana.",
				nameTooLong: "Nazwa może mieć maks. {max} znaków.",
				typeRequired: "Wybierz typ.",
				customTypeRequired: "Opisz typ.",
				customTypeTooLong: "Typ własny może mieć maks. {max} znaków.",
				descriptionTooLong: "Opis może mieć maks. {max} znaków.",
				unknown: "Coś poszło nie tak. Spróbuj ponownie.",
			},
		},
		detail: {
			breadcrumbsNavLabel: "Ścieżka nawigacji",
			manageLabel: "Zarządzaj",
			manageMenu: {
				edit: "Edytuj nieruchomość",
				transfer: "Przekaż własność",
			},
		},
		edit: {
			title: 'Edytuj "{propertyName}"',
			nameLabel: "Nazwa",
			descriptionLabel: "Opis",
			save: "Zapisz zmiany",
			saving: "Zapisywanie...",
			cancel: "Anuluj",
			readOnlyNotice:
				"Tylko właściciel lub członkowie z rolą architekta mogą edytować tę nieruchomość.",
			improveWithAi: "Ulepsz z AI",
			addDescription: "Dodaj opis…",
			editName: "Edytuj nazwę",
			editDescription: "Edytuj opis",
			descriptionPlaceholder: "Opisz tę nieruchomość w Markdown…",
			deleteSectionTitle: "Usuń nieruchomość",
			deleteSectionBody:
				"Ta operacja usuwa nieruchomość z Twojego konta. Nie można jej cofnąć.",
			deleteAction: "Usuń nieruchomość",
			deleteConfirmTitle: "Usunąć nieruchomość?",
			deleteConfirmBody:
				'"{propertyName}" zniknie z Twojego konta. Tej operacji nie można cofnąć.',
			deleteConfirmAction: "Usuń trwale",
			deleteError: "Nie udało się usunąć nieruchomości.",
		},
		transfer: {
			title: 'Przekaż własność "{propertyName}"',
			explainer:
				"Wybierz członka tej nieruchomości na nowego właściciela. Pozostaniesz z dostępem jako członek w swojej obecnej roli. Cofnięcie wymaga działania nowego właściciela.",
			memberLabel: "Członek",
			emptyState:
				"Brak członków do przekazania. Najpierw zaproś kogoś ze strony nieruchomości.",
			confirmationLabel: "Wpisz nazwę nieruchomości, aby potwierdzić",
			confirmationPlaceholder: "{propertyName}",
			submit: "Przekaż własność do {name}",
			submitting: "Przekazywanie...",
			cancel: "Anuluj",
		},
		share: {
			buttonLabel: "Udostępnij",
			or: "Lub",
			modalTitle: "Udostępnij „{propertyName}”",
			invite: {
				emailLabel: "E-mail",
				emailPlaceholder: "imie@example.com",
				roleLabel: "Rola",
				submit: "Wyślij zaproszenie",
				submitting: "Wysyłanie...",
				confirm: {
					title: "Potwierdź zaproszenie",
					body: "Zaprosić {email} jako {role}?",
					send: "Wyślij zaproszenie",
					cancel: "Anuluj",
				},
				errors: {
					emailInvalid: "Podaj prawidłowy adres e-mail.",
					selfInvite:
						"Nie możesz zaprosić samego siebie — jesteś właścicielem.",
					alreadyMember: "{email} jest już członkiem.",
					unknown: "Nie udało się wysłać zaproszenia. Spróbuj ponownie.",
				},
			},
			members: {
				heading: "Członkowie",
				ownerLabel: "Właściciel",
				remove: {
					button: "Usuń członka",
					submitting: "Usuwanie...",
					confirm: {
						title: "Usuń członka",
						body: "Usunąć {name} z tej nieruchomości?",
						remove: "Usuń",
						cancel: "Anuluj",
					},
					errors: {
						unknown: "Nie udało się usunąć członka.",
					},
				},
				role: {
					button: "Zmień rolę członka",
					errors: {
						unknown: "Nie udało się zmienić roli członka.",
					},
				},
			},
			invitations: {
				heading: "Oczekujące zaproszenia",
				emptyState: "Brak oczekujących zaproszeń.",
				expiresIn: "Wygasa za {days} dni",
				expiresToday: "Wygasa dzisiaj",
				menu: {
					actionsLabel: "Akcje zaproszenia",
				},
				remove: {
					button: "Usuń zaproszenie",
					submitting: "Usuwanie...",
					confirm: {
						title: "Usuń zaproszenie",
						body: "Anulować zaproszenie dla {name}?",
						remove: "Usuń zaproszenie",
						cancel: "Anuluj",
					},
					errors: {
						unknown: "Nie udało się usunąć zaproszenia.",
					},
				},
				resend: {
					button: "Wyślij ponownie",
					submitting: "Wysyłanie…",
					confirm: {
						title: "Wyślij zaproszenie ponownie",
						body: "Wysłać zaproszenie ponownie do {email}?",
						send: "Wyślij ponownie",
						cancel: "Anuluj",
					},
					errors: {
						unknown: "Nie udało się wysłać zaproszenia ponownie.",
					},
				},
				role: {
					button: "Zmień rolę zaproszenia",
					errors: {
						unknown: "Nie udało się zmienić roli zaproszenia.",
					},
				},
			},
			roles: {
				member: "Członek",
				contractor: "Wykonawca",
				architect: "Architekt",
			},
			roleDescriptions: {
				member:
					"Ogólny uczestnik — informowany na bieżąco o tej nieruchomości.",
				contractor:
					"Wykonawca remontowy lub budowlany pracujący przy tej nieruchomości.",
				architect:
					"Projektant odpowiedzialny za decyzje projektowe dotyczące tej nieruchomości.",
			},
			qr: {
				generateButton: "Wygeneruj QR",
				dialogTitle: "QR zaproszenia — {role}",
				pinLabel: "PIN",
				instructions:
					"Poproś zaproszonego, aby zeskanował ten kod w swojej aplikacji zPlanem i wpisał PIN.",
				instructionsNoPin:
					"Poproś zaproszonego, aby zeskanował ten kod w swojej aplikacji zPlanem — jego prośbę zatwierdzisz tutaj.",
				expiresInMmSs: "Wygasa za {mm}:{ss}",
				expired: "Wygasło",
				done: "Gotowe",
				generating: "Generowanie...",
				error: "Nie udało się wygenerować zaproszenia QR. Spróbuj ponownie.",
			},
		},
	},

	projects: {
		list: {
			heading: "Projekty",
			createCta: "Nowy projekt",
			emptyStateTitle: "Brak projektów",
			emptyStateBody: "Utwórz pierwszy projekt dla tej nieruchomości.",
			archivedHeading: "Zarchiwizowane",
			archivedBadge: "Zarchiwizowany",
		},
		statuses: {
			draft: "Szkic",
			ready_for_work: "Gotowy do pracy",
			in_progress: "W trakcie",
			awaiting_acceptance: "Oczekuje na akceptację",
			accepted: "Zaakceptowany",
		},
		types: {
			garden: "Ogród",
			construction: "Budowa",
			interior: "Wnętrze",
		},
		tabs: {
			navLabel: "Sekcje projektu",
			details: "Szczegóły",
			tasks: "Zadania",
			visualizations: "Wizualizacje i plany",
			visualizationsShort: "Wizualizacje",
			inspirations: "Inspiracje",
			documents: "Dokumenty",
			budget: "Budżet",
			members: "Członkowie",
		},
		form: {
			createTitle: "Nowy projekt",
			nameLabel: "Nazwa",
			namePlaceholder: "np. Metamorfoza ogrodu",
			typeLabel: "Typ",
			descriptionLabel: "Opis",
			descriptionPlaceholder: "Opcjonalne notatki o projekcie",
			submit: "Utwórz projekt",
			cancel: "Anuluj",
			errors: {
				nameRequired: "Nazwa jest wymagana",
				nameTooLong: "Nazwa może mieć najwyżej {max} znaków",
				typeRequired: "Wybierz typ projektu",
				descriptionTooLong: "Opis może mieć najwyżej {max} znaków",
				costInvalid: "Podaj poprawną kwotę (np. 12500.00)",
				unknown: "Coś poszło nie tak. Spróbuj ponownie.",
			},
		},
		detail: {
			statusLabel: "Status",
			acceptedOwnerOnlyHint:
				"Tylko właściciel nieruchomości może oznaczyć projekt jako zaakceptowany",
			membersHeading: "Członkowie",
			addMemberCta: "Dodaj członka",
			addMemberTitle: "Dodaj członka projektu",
			addMemberEmpty: "Wszyscy uprawnieni są już w tym projekcie",
			removeMemberLabel: "Usuń",
			archiveCta: "Archiwizuj",
			unarchiveCta: "Przywróć",
			archivedNotice: "Ten projekt jest zarchiwizowany",
			editCta: "Edytuj",
			editTitle: "Edytuj projekt",
			save: "Zapisz",
			saving: "Zapisywanie…",
			cancel: "Anuluj",
			estimatedExecutionCostLabel: "Szacunkowy koszt wykonania",
			costPlaceholder: "np. 12500.00",
			currencyLabel: "Waluta",
			noDescription: "Brak opisu",
			descriptionHeading: "Opis",
			journalHeading: "Dziennik",
		},
		cost: {
			heading: "Koszt stworzenia projektu",
			noCost: "Nie zaproponowano jeszcze kosztu",
			acceptedLabel: "Zaakceptowany koszt",
			pendingLabel: "Oczekująca propozycja",
			proposeCta: "Zaproponuj koszt",
			proposeTitle: "Zaproponuj koszt",
			amountLabel: "Kwota",
			amountPlaceholder: "np. 12500.00",
			currencyLabel: "Waluta",
			submit: "Zaproponuj",
			cancel: "Anuluj",
			acceptCta: "Akceptuj",
			rejectCta: "Odrzuć",
			markPaidCta: "Oznacz jako opłacony",
			markPaidHint: "Dostępne po zaakceptowaniu kosztu",
			paidBadge: "Opłacony",
			errors: {
				amountInvalid: "Podaj poprawną kwotę (np. 12500.00)",
				unknown: "Coś poszło nie tak. Spróbuj ponownie.",
			},
		},
		visualizations: {
			emptyTitle: "Brak wizualizacji",
			emptyBody: "Tutaj pojawią się rendery, rysunki i plany tego projektu.",
			addCta: "Dodaj wizualizację",
			processing: "Przetwarzanie…",
			modalTitle: "Dodaj wizualizację",
			tabFile: "Prześlij plik",
			tabLink: "Dodaj link",
			chooseFile: "Wybierz plik",
			dropHere: "Przeciągnij pliki tutaj lub kliknij, aby wybrać",
			fileHint: "Obrazy, PDF-y lub wideo — do 25 MB na plik.",
			urlLabel: "Link",
			urlPlaceholder: "https://…",
			titleLabel: "Tytuł (opcjonalnie)",
			descriptionLabel: "Opis (opcjonalnie)",
			add: "Dodaj",
			adding: "Dodawanie…",
			cancel: "Anuluj",
			download: "Pobierz",
			delete: "Usuń",
			deleteTitle: "Usunąć wizualizację?",
			deleteBody: "Usunie ją na stałe i nie można tego cofnąć.",
			close: "Zamknij",
			errorUnsupported: "Ten typ pliku nie jest obsługiwany.",
			errorTooLarge: "Ten plik przekracza limit 25 MB.",
			errorUpload: "Nie udało się dodać wizualizacji. Spróbuj ponownie.",
			errorLoad: "Nie udało się wczytać wizualizacji.",
			uploadedByLabel: "Przesłane przez {name}",
		},
		inspirations: {
			emptyTitle: "Brak inspiracji",
			emptyBody: "Zbieraj tu referencje i pomysły dla tego projektu.",
			addCta: "Dodaj inspirację",
			processing: "Przetwarzanie…",
			modalTitle: "Dodaj inspirację",
			tabFile: "Prześlij plik",
			tabLink: "Dodaj link",
			chooseFile: "Wybierz plik",
			dropHere: "Przeciągnij pliki tutaj lub kliknij, aby wybrać",
			fileHint: "Obrazy, PDF-y lub wideo — do 5 MB na plik.",
			urlLabel: "Link",
			urlPlaceholder: "https://…",
			titleLabel: "Tytuł (opcjonalnie)",
			descriptionLabel: "Opis (opcjonalnie)",
			add: "Dodaj",
			adding: "Dodawanie…",
			cancel: "Anuluj",
			download: "Pobierz",
			delete: "Usuń",
			deleteTitle: "Usunąć inspirację?",
			deleteBody: "Spowoduje to trwałe usunięcie — nie można tego cofnąć.",
			close: "Zamknij",
			errorUnsupported: "Ten typ pliku nie jest obsługiwany.",
			errorTooLarge: "Plik przekracza limit 5 MB.",
			errorUpload: "Nie udało się dodać inspiracji. Spróbuj ponownie.",
			errorLoad: "Nie udało się wczytać inspiracji.",
			uploadedByLabel: "Przesłane przez {name}",
		},
		mediaUpload: {
			filesSelected: {
				one: "Wybrano {count} plik",
				few: "Wybrano {count} pliki",
				many: "Wybrano {count} plików",
				other: "Wybrano {count} pliku",
			},
			addFiles: {
				one: "Dodaj {count} plik",
				few: "Dodaj {count} pliki",
				many: "Dodaj {count} plików",
				other: "Dodaj {count} pliku",
			},
			addingFiles: {
				one: "Dodawanie {count} pliku…",
				few: "Dodawanie {count} plików…",
				many: "Dodawanie {count} plików…",
				other: "Dodawanie {count} pliku…",
			},
			filesSkipped: {
				one: "Pominięto {count} plik",
				few: "Pominięto {count} pliki",
				many: "Pominięto {count} plików",
				other: "Pominięto {count} pliku",
			},
			filesFailed: {
				one: "Nie udało się dodać {count} pliku",
				few: "Nie udało się dodać {count} plików",
				many: "Nie udało się dodać {count} plików",
				other: "Nie udało się dodać {count} pliku",
			},
			addAnother: "Dodaj kolejny plik",
			removeFile: "Usuń {name}",
			retryFailed: "Spróbuj ponownie",
			statusUploading: "Przesyłanie…",
			statusDone: "Dodano",
			statusFailed: "Nie udało się",
			errorDuplicate: "Jest już na liście.",
			errorBatchLimit: "Możesz dodać maksymalnie {max} plików naraz.",
			errorQuotaFull: "Ten projekt osiągnął limit {max} elementów.",
		},
		budget: {
			title: "Budżet",
			spentLabel: "Potwierdzone wydatki",
			excludedNote: "{count} bez kursu — suma jest częściowa",
			draftsLabel: "do przejrzenia",
			emptyTitle: "Brak wydatków",
			emptyBody:
				"Zapisuj faktyczne wydatki — dodaj wydatek ręcznie albo prześlij fakturę, a Mareczek ją odczyta.",
			addCta: "Dodaj wydatek",
			captureCta: "Prześlij fakturę",
			formTitleAdd: "Dodaj wydatek",
			formTitleEdit: "Edytuj wydatek",
			amountLabel: "Kwota",
			currencyLabel: "Waluta",
			kindLabel: "Kategoria",
			kindNone: "Bez kategorii",
			vendorLabel: "Sprzedawca",
			descriptionLabel: "Opis",
			occurredAtLabel: "Data",
			projectLabel: "Projekt",
			projectNone: "Cała nieruchomość",
			save: "Zapisz",
			cancel: "Anuluj",
			amountInvalid: "Podaj poprawną kwotę (np. 1250.50)",
			kinds: {
				material: "Materiały",
				labor: "Robocizna",
				service: "Usługi",
				equipment: "Sprzęt",
				mix: "Mieszane",
				other: "Inne",
			},
			statuses: {
				ai_draft: "Szkic",
				confirmed: "Potwierdzony",
				rejected: "Odrzucony",
			},
			accept: "Zatwierdź",
			reject: "Odrzuć",
			edit: "Edytuj",
			delete: "Usuń",
			deleteConfirmBody: "Usunąć ten wydatek?",
			captureModalTitle: "Prześlij fakturę",
			captureHint:
				"Faktura lub paragon — PDF albo zdjęcie. Mareczek odczyta kwotę, sprzedawcę i datę — zatwierdzasz zanim trafi do sumy.",
			fileLabel: "Plik faktury (PDF lub zdjęcie)",
			capture: "Prześlij",
			capturing: "Przesyłanie…",
			duplicateWarning: "Wygląda na to, że ta faktura została już przesłana.",
			extractionPending: "Odczytywanie faktury…",
			proofLabel: "Faktura",
			errorLoad: "Nie udało się wczytać budżetu.",
			errorSave: "Nie udało się zapisać wydatku.",
			errorFileTooLarge: "Ten plik jest zbyt duży.",
		},
		documents: {
			emptyTitle: "Brak dokumentów",
			emptyBody:
				"Prześlij PDF-y, arkusze kalkulacyjne lub inne pliki do tego projektu.",
			emptyBodyProperty:
				"Prześlij PDF-y, arkusze kalkulacyjne lub inne pliki do tej nieruchomości.",
			addCta: "Dodaj dokument",
			uploadModalTitle: "Prześlij dokument",
			titleLabel: "Tytuł",
			titlePlaceholder: "np. Umowa",
			fileLabel: "Plik",
			aiReadableLabel: "Pozwól Mareczek (AI) odczytać ten plik",
			upload: "Prześlij",
			uploading: "Przesyłanie…",
			cancel: "Anuluj",
			delete: "Usuń",
			deleteTitle: "Usunąć ten dokument?",
			deleteBody:
				"Spowoduje to trwałe usunięcie \u201e{title}\u201d \u2014 nie można tego cofnąć.",
			deleteConfirm: "Usuń dokument",
			download: "Pobierz",
			open: "Otwórz",
			scopeProject: "Projekt",
			scopeProperty: "Nieruchomość",
			allInProperty: "Wszystkie dokumenty w nieruchomości",
			scopeFieldLabel: "Przypisz do",
			documentTypeLabel: "Typ dokumentu",
			documentTypeOptions: {
				contract: "Umowa",
				agreement: "Porozumienie",
				notice: "Wypowiedzenie",
				bill: "Rachunek",
				land_report: "Raport o terenie",
				other: "Inny",
			},
			fileDropHint: "Przeciągnij plik tutaj lub kliknij, aby wybrać",
			grantsModalTitle: "Udostępnij dokument",
			grantsCta: "Udostępnij",
			ownerLabel: "Właściciel",
			grantToggleGrant: "Nadaj dostęp",
			grantToggleRevoke: "Cofnij dostęp",
			errorLoad: "Nie udało się wczytać dokumentów.",
			errorFileTooLarge: "Plik przekracza limit 50 MB.",
			errorUpload: "Przesyłanie nie powiodło się.",
			editCta: "Edytuj dokument",
			editModalTitle: "Edytuj dokument",
			save: "Zapisz",
			errorDownload: "Nie udało się otworzyć pliku. Spróbuj ponownie.",
			errorDownloadBlocked:
				"Zezwól na wyskakujące okienka, aby otwierać dokumenty.",
			aiReadableHint:
				"Mareczek analizuje dokumenty oznaczone jako Umowa (ryzyka, brakujące zabezpieczenia, potencjalnie abuzywne klauzule) lub Raport o terenie (ograniczenia i ryzyka działki); pozostałe typy dokumentów nie są analizowane.",
			aiUploadNoticeUnsupported:
				"AI odczytuje tylko pliki PDF i obrazy — plik zostanie przesłany, ale Mareczek go nie przetworzy.",
			aiUploadNoticeTooLarge:
				"Plik przekracza limit {size} dla analizy AI — zostanie przesłany, ale Mareczek go nie przeanalizuje.",
			aiEditNoticeTooLarge:
				"Plik przekracza limit {size} dla analizy AI, więc Mareczek go nie przeanalizuje.",
			aiBadgeReviewing: "Analizuję",
			aiBadgeReviewingHint: "Mareczek analizuje ten dokument.",
			aiBadgeReview: "Analiza AI",
			aiBadgeReviewHint: "Zobacz analizę od Mareczka.",
			aiBadgeFailed: "Analiza nieudana",
			aiBadgeFailedHint:
				"Mareczek nie ukończył tej analizy. Otwórz, aby zobaczyć szczegóły.",
			aiBadgeUnsupported: "Nieobsługiwany plik",
			aiBadgeUnsupportedHint: "AI odczytuje tylko pliki PDF i obrazy.",
			aiBadgeTooLarge: "Za duży",
			aiBadgeTooLargeHint: "Plik jest za duży do analizy AI (limit {size}).",
			aiReviewModalTitle: "Analiza dokumentu od Mareczka",
			aiReviewProcessing: "Mareczek analizuje ten dokument",
			aiReviewFailed:
				"Mareczek nie ukończył tej analizy. Wyłącz i włącz ponownie analizę AI albo prześlij plik jeszcze raz, aby spróbować ponownie.",
			aiReviewFailedNotContract:
				"Ten dokument nie wygląda na umowę, więc nie ma czego analizować.",
			aiReviewFailedNotLandReport:
				"Ten dokument nie wygląda na raport o terenie, więc nie ma czego analizować.",
			aiReviewFailedUnreadable:
				"Mareczek nie mógł odczytać tego dokumentu. Może być zeskanowany, zaszyfrowany lub uszkodzony.",
			aiReviewSummary: "Podsumowanie",
			aiReviewCritical: "Krytyczne problemy",
			aiReviewAdvantages: "Na Twoją korzyść",
			aiReviewDisadvantages: "Na Twoją niekorzyść",
			aiReviewMissing: "Brakujące zabezpieczenia",
			aiReviewPossible: "Warto wyjaśnić",
			aiReviewAbusive: "Potencjalnie abuzywne klauzule",
			aiReviewNoFindings: "Nic tu nie zgłoszono.",
			aiReviewDisclaimer:
				"Mareczek to asystent AI, a nie prawnik. Przed podjęciem decyzji zweryfikuj wszystko, co istotne.",
			close: "Zamknij",
		},
	},

	inviteEmail: {
		subject: "Zaproszenie do {propertyName} na zPlanem",
		greeting: "Cześć,",
		body: "{inviterName} zaprasza Cię do współpracy nad {propertyName}. Kliknij link poniżej, aby zaakceptować zaproszenie.",
		cta: "Akceptuj zaproszenie",
		signature: "— Zespół zPlanem",
		expiryNote: "To zaproszenie wygasa za {days} dni.",
	},

	mentionEmail: {
		subject: "{byDisplayName} wspomniał(a) o Tobie w {propertyName}",
		greeting: "Cześć,",
		body: "{byDisplayName} wspomniał(a) o Tobie na czacie nieruchomości {propertyName}, a wiadomość wciąż czeka na Ciebie. Otwórz czat, aby ją nadrobić.",
		cta: "Otwórz czat",
		signature: "— Zespół zPlanem",
	},

	invitationAcceptedEmail: {
		subject: "{accepterName} dołączył(a) do {propertyName}",
		greeting: "Cześć,",
		body: "{accepterName} zaakceptował(a) Twoje zaproszenie do {propertyName} jako {role}.",
		signature: "— Zespół zPlanem",
	},

	activityDigestEmail: {
		subject: "zPlanem — podsumowanie aktywności {date}",
		intro: "Oto co zmieniło się w Twoich nieruchomościach od wczoraj, 8:00:",
		openJournal: "Otwórz dziennik",
	},

	documentSharedEmail: {
		granteeSubject: "{actor} udostępnił(a) Ci dokument: {title}",
		granteeBody: "{actor} udostępnił(a) Ci dokument \u201e{title}\u201d.",
		ownerSubject: "Twój dokument został udostępniony: {title}",
		ownerBody:
			"{actor} udostępnił(a) Twój dokument \u201e{title}\u201d użytkownikowi {grantee}.",
		cta: "Otwórz dokumenty",
	},

	acceptInvitation: {
		title: "{inviterName} zaprasza Cię do {propertyName} jako {role}",
		loadingState: "Ładowanie zaproszenia...",
		terminalErrors: {
			notFound: "Ten link zaproszenia jest już nieaktualny.",
			expired:
				"To zaproszenie wygasło. Poproś właściciela nieruchomości o wysłanie nowego.",
			alreadyAccepted: "To zaproszenie zostało już zaakceptowane.",
		},
		emailMismatch:
			"To zaproszenie zostało wysłane na adres {invitationEmail}. Wyloguj się i zaloguj na ten adres, aby je zaakceptować.",
		signInPrompt: "Zaloguj się jako {invitationEmail}, aby zaakceptować",
		accept: "Akceptuj zaproszenie",
		accepting: "Akceptowanie...",
		success: "Dołączyłeś do {propertyName}!",
		backToDashboard: "Przejdź do panelu",
		goToApp: "Przejdź do aplikacji",
		unknownInviter: "Ktoś",
		qrOpenInAppTitle:
			"Otwórz aplikację zPlanem na telefonie, aby dołączyć do {propertyName}",
		qrOpenInAppBody:
			"To zaproszenie QR jest doręczane osobiście — zakończ je, skanując kod QR w mobilnej aplikacji zPlanem.",
	},

	aiIndicator: {
		processing: "AI przetwarza",
		processed: "AI przetworzone",
		failed: "AI nie powiodło się",
	},

	notFound: {
		title: "Ta strona nie istnieje",
	},

	chat: {
		headerName: "Mareczek",
		headerSubtitle: "Asystent AI",
		inputPlaceholder: "Napisz do Mareczka…",
		emptyState: "Rozpocznij rozmowę",
		sendButtonLabel: "Wyślij wiadomość",
		openButtonLabel: "Otwórz czat",
		minimizeButtonLabel: "Zminimalizuj czat",
		expandButtonLabel: "Powiększ czat",
		collapseButtonLabel: "Zmniejsz czat",
		thinkingLabel: "Mareczek pracuje…",
		unreadBadge: {
			cap: "99+",
			label: "{count} nieprzeczytanych wiadomości",
		},
		shared: {
			headerName: "Czat członków",
			propertyHeaderName: "Czat nieruchomości",
			projectHeaderName: "Czat projektu",
			composerPlaceholder: "Napisz wiadomość…",
			mentionSuggestionsLabel: "Podpowiedzi wzmianek",
			emptyState: "Jeszcze brak wiadomości.",
			sendButtonLabel: "Wyślij",
			closeLabel: "Zamknij czat członków",
			memberCountSingular: "{count} członek",
			memberCountPlural: "{count} członków",
			deletedUser: "Usunięty użytkownik",
		},
		errors: {
			sendFailed: "Nie udało się wysłać wiadomości. Spróbuj ponownie.",
			loadFailed: "Nie udało się załadować wiadomości. Spróbuj ponownie.",
		},
		mention: {
			toastTitle: "{name} wspomniał(a) o Tobie",
		},
	},

	mareczek: {
		greetings: {
			propertyCreated:
				"Cześć {ownerName}! Jestem Mareczek, Twój asystent AI w zPlanem. Pomogę Ci planować i zarządzać nieruchomością {propertyName} — pisz do mnie, kiedy tylko zechcesz.",
			propertyJoined:
				"Cześć {memberName}! Witaj w {propertyName}. Jestem Mareczek, Twój asystent AI w zPlanem — pisz do mnie, kiedy tylko zechcesz.",
		},
		systemMessages: {
			memberJoined: "{fullName} dołączył(a) do nieruchomości jako {role}.",
		},
		agentActions: {
			executed: "Gotowe — akcja została wykonana.",
			executedSummary: "Zrobione — {summary}.",
			failed: "Nie udało się wykonać akcji.",
			declined: "Akcja odrzucona.",
			chainFailed:
				"Coś poszło nie tak po mojej stronie — nie udało mi się dokończyć tej odpowiedzi.",
			card: {
				titles: {
					"create-project": "Utwórz projekt",
					"update-project": "Zaktualizuj projekt",
					"propose-cost": "Zaproponuj koszt",
					"archive-project": "Zarchiwizuj projekt",
					"invite-member": "Zaproś do nieruchomości",
					"resend-invitation": "Wyślij ponownie zaproszenie",
					"create-ticket": "Utwórz zgłoszenie",
					"update-ticket": "Zaktualizuj zgłoszenie",
					"assign-ticket": "Przypisz zgłoszenie",
					"set-ticket-status": "Zmień status zgłoszenia",
					"set-ticket-reminder": "Ustaw przypomnienie zgłoszenia",
					"add-ticket-comment": "Dodaj komentarz do zgłoszenia",
					"delete-ticket": "Usuń zgłoszenie",
					"delete-ticket-comment": "Usuń komentarz zgłoszenia",
					"remove-ticket-attachment": "Usuń załącznik zgłoszenia",
					"rename-property": "Zmień nazwę nieruchomości",
					"update-property-description": "Zmień opis nieruchomości",
					"create-inspiration-from-url": "Dodaj inspirację z linku",
					"create-visualization-from-url": "Dodaj wizualizację z linku",
					"create-budget-entry": "Zapisz wydatek",
					"update-budget-entry": "Zmień wydatek",
					"delete-budget-entry": "Usuń wydatek",
					generic: "Proponowana akcja",
				},
				accept: "Zatwierdź",
				decline: "Odrzuć",
				statusAccepted: "Zatwierdzono",
				statusDeclined: "Odrzucono",
				statusExpired: "Wygasło",
				statusAwaitingOwner: "Czeka na zatwierdzenie przez osobę zlecającą",
			},
		},
	},

	notificationsBell: {
		title: "Powiadomienia",
		close: "Zamknij",
		empty: "Nic nowego",
		markAllRead: "Oznacz wszystkie jako przeczytane",
		chatMention: "{actor} wspomniał(a) o Tobie na czacie",
		ticketAssigned: "{actor} przypisał(a) Ci zadanie \u201e{title}\u201d",
		joinRequestCreated: "{actor} prosi o dołączenie do Twojej nieruchomości",
		agentActionPending:
			"Mareczek przygotował akcję czekającą na Twoją akceptację",
	},

	mediaModal: {
		prev: "Poprzedni element",
		next: "Następny element",
	},

	journal: {
		emptyTitle: "Brak aktywności",
		emptyDescription: "Wydarzenia pojawią się tutaj, gdy nastąpią.",
		errorTitle: "Nie udało się załadować dziennika",
		errorRetry: "Spróbuj ponownie",
		deletedUser: "Usunięty użytkownik",
		sourceProperty: "Nieruchomość",
		events: {
			propertyCreated: {
				label: "Nieruchomość utworzona",
				aria: "Nieruchomość utworzona przez {actor}",
			},
			memberInvitedEmail: {
				label: "Wysłano zaproszenie e-mail",
				aria: "{actor} wysłał(a) zaproszenie e-mail",
			},
			memberInvitedQr: {
				label: "Wygenerowano zaproszenie QR",
				aria: "{actor} wygenerował(a) zaproszenie QR",
			},
			memberJoined: {
				label: "Dołączył(a) jako {role}",
				aria: "{actor} dołączył(a) jako {role}",
			},
			unknown: {
				label: "Aktywność",
				aria: "Zarejestrowana aktywność",
			},
			projectCreated: {
				label: "Utworzono projekt",
				aria: "{actor} utworzył(a) projekt",
			},
			projectStatusChanged: {
				label: "Zmiana statusu: {from} → {to}",
				aria: "{actor} zmienił(a) status z {from} na {to}",
			},
			projectCostProposed: {
				label: "Zaproponowano koszt: {amount} {currency}",
				aria: "{actor} zaproponował(a) koszt {amount} {currency}",
			},
			projectCostAccepted: {
				label: "Zaakceptowano koszt: {amount} {currency}",
				aria: "{actor} zaakceptował(a) koszt {amount} {currency}",
			},
			projectCostRejected: {
				label: "Odrzucono koszt: {amount} {currency}",
				aria: "{actor} odrzucił(a) koszt {amount} {currency}",
			},
			projectPaid: {
				label: "Oznaczono jako opłacony: {amount} {currency}",
				aria: "{actor} oznaczył(a) projekt jako opłacony ({amount} {currency})",
			},
			projectMemberAdded: {
				label: "Dodano do projektu: {target}",
				aria: "{actor} dodał(a) {target} do projektu",
			},
			projectMemberRemoved: {
				label: "Usunięto z projektu: {target}",
				aria: "{actor} usunął(ęła) {target} z projektu",
			},
			projectArchived: {
				label: "Zarchiwizowano projekt",
				aria: "{actor} zarchiwizował(a) projekt",
			},
			projectUnarchived: {
				label: "Przywrócono projekt",
				aria: "{actor} przywrócił(a) projekt",
			},
			inspirationCreated: {
				label: "Dodano inspirację",
				aria: "{actor} dodał(a) inspirację",
			},
			inspirationDeleted: {
				label: "Usunięto inspirację",
				aria: "{actor} usunął(usunęła) inspirację",
			},
			visualizationCreated: {
				label: "Dodano wizualizację",
				aria: "{actor} dodał(a) wizualizację",
			},
			visualizationDeleted: {
				label: "Usunięto wizualizację",
				aria: "{actor} usunął(usunęła) wizualizację",
			},
			documentCreated: {
				label: "Dodano dokument",
				aria: "{actor} dodał(a) dokument",
			},
			documentDeleted: {
				label: "Usunięto dokument",
				aria: "{actor} usunął(usunęła) dokument",
			},
			documentUpdated: {
				label: "Zaktualizowano dokument",
				aria: "{actor} zaktualizował(a) dokument",
			},
			ticketCreated: {
				label: "Dodano zadanie: {title}",
				aria: "{actor} dodał(a) zadanie: {title}",
			},
			ticketCreatedDefect: {
				label: "Zgłoszono usterkę: {title}",
				aria: "{actor} zgłosił(a) usterkę: {title}",
			},
			ticketStatusChanged: {
				label: "{title}: {from} → {to}",
				aria: "{actor} zmienił(a) status {title} z {from} na {to}",
			},
			ticketAssigned: {
				label: "{title} → {assignee}",
				aria: "{actor} przypisał(a) {title} do {assignee}",
			},
			ticketUnassigned: {
				label: "{title}: bez przypisania",
				aria: "{actor} usunął(-ęła) przypisanie {title}",
			},
		},
	},

	tickets: {
		emptyTitle: "Brak zadań",
		emptyDescription: "Zadania pojawią się tutaj, gdy zostaną dodane.",
		emptyFilteredTitle: "Brak zadań dla tych filtrów",
		emptyFilteredDescription:
			"Wyczyść filtry, aby zobaczyć wszystkie zadania w tym zakresie.",
		errorTitle: "Nie udało się załadować zadań",
		errorRetry: "Spróbuj ponownie",
		createCta: "Dodaj zadanie",
		createTitle: "Nowe zadanie",
		titleLabel: "Tytuł",
		titlePlaceholder: "np. Zamówić płytki",
		kindLabel: "Typ",
		assigneeSelectLabel: "Przypisanie",
		submitCreate: "Dodaj",
		creating: "Dodawanie...",
		statusLabel: "Status",
		assignChangeError: "Nie udało się zmienić przypisania",
		statusChangeError: "Nie udało się zmienić statusu",
		loadMore: "Pokaż więcej",
		loadingMore: "Ładowanie...",
		boardAriaLabel: "Tablica zadań",
		viewSwitcherLabel: "Widok zadań",
		viewBoard: "Tablica",
		viewList: "Lista",
		listAriaLabel: "Lista zadań",
		unassigned: "Bez przypisania",
		dueLabel: "Termin",
		createdLabel: "Utworzono",
		updatedLabel: "Zaktualizowano",
		assigneeLabel: "Osoba odpowiedzialna",
		reminderLabel: "Przypomnienie",
		reminderSave: "Zapisz",
		reminderClear: "Wyczyść",
		reminderChangeError: "Nie udało się zmienić przypomnienia",
		descriptionTitle: "Opis",
		noDescription: "Brak opisu",
		backToList: "Wróć do zadań",
		backToMyTasks: "Wróć do moich zadań",
		backToProjectTasks: "Wróć do zadań projektu",
		attachmentsTitle: "Załączniki",
		noAttachments: "Brak załączników",
		attachmentUpload: "Dodaj plik",
		attachmentUploading: "Przesyłanie...",
		attachmentOpen: "Otwórz załącznik",
		attachmentDelete: "Usuń",
		attachmentUploadError: "Nie udało się dodać załącznika",
		attachmentDeleteError: "Nie udało się usunąć załącznika",
		commentsTitle: "Komentarze",
		commentsEmpty: "Brak komentarzy",
		commentPlaceholder: "Dodaj komentarz",
		commentSubmit: "Dodaj komentarz",
		commentSubmitting: "Dodawanie...",
		commentCreateError: "Nie udało się dodać komentarza",
		commentEdit: "Edytuj",
		commentSave: "Zapisz",
		commentCancel: "Anuluj",
		commentEditError: "Nie udało się zaktualizować komentarza",
		commentDelete: "Usuń",
		commentDeleteError: "Nie udało się usunąć komentarza",
		commentEdited: "edytowano",
		mentionSuggestionsLabel: "Podpowiedzi wzmianek",
		myTicketsTitle: "Moje zadania",
		myTicketsEmptyTitle:
			"Brak zadań przypisanych do Ciebie lub utworzonych przez Ciebie",
		myTicketsEmptyDescription:
			"Zadania przypisane do Ciebie lub utworzone przez Ciebie pojawią się tutaj.",
		myTicketsFilteredEmptyTitle: "Brak zadań dla tych filtrów",
		myTicketsFilteredEmptyDescription:
			"Wyczyść filtry, aby zobaczyć wszystkie swoje zadania.",
		myTicketsAllStatuses: "Wszystkie",
		filtersLabel: "Filtry zadań",
		filterProperty: "Nieruchomość",
		filterProject: "Projekt",
		filterAssignee: "Przypisanie",
		filterTopic: "Temat",
		filterKind: "Typ",
		filterAllProperties: "Wszystkie nieruchomości",
		filterAllProjects: "Wszystkie projekty",
		filterAllAssignees: "Wszystkie osoby",
		filterAllTopics: "Wszystkie tematy",
		filterAllKinds: "Wszystkie typy",
		filterClear: "Wyczyść",
		statusCountLabel: "Status {status}: {count}",
		topicUngrouped: "Bez tematu",
		topicManage: "Tematy",
		topicCreate: "Dodaj temat",
		topicRename: "Zmień nazwę",
		topicDelete: "Usuń",
		topicDeleteConfirm: "Usunąć ten temat?",
		topicNameLabel: "Nazwa tematu",
		topicNamePlaceholder: "np. Kuchnia",
		topicScopeLabel: "Zakres",
		topicScopeProperty: "Cała nieruchomość",
		topicCreateError: "Nie udało się dodać tematu",
		topicUpdateError: "Nie udało się zapisać tematu",
		topicDeleteError: "Nie udało się usunąć tematu",
		status: {
			open: "Otwarte",
			in_progress: "W toku",
			done: "Zrobione",
			cancelled: "Anulowane",
		},
		kind: {
			task: "Zadanie",
			defect: "Usterka",
		},
		formErrors: {
			titleRequired: "Podaj tytuł zadania",
			titleTooLong: "Tytuł może mieć maksymalnie {max} znaków",
			descriptionTooLong: "Opis może mieć maksymalnie {max} znaków",
			unknown: "Nie udało się zapisać zadania",
		},
	},

	userMainRoles: {
		investor: "Inwestor",
		contractor: "Wykonawca",
		architect: "Architekt",
	},

	apiErrors: {
		auth: {
			unauthorized: "Brak autoryzacji",
			forbidden: "Nie masz uprawnień do tej operacji",
			onboardingRequired: "Wymagana rejestracja",
			emailRequired: "Adres e-mail jest wymagany",
			emailAlreadyAssociated: "Adres e-mail jest już powiązany z innym kontem",
		},
		validation: {
			failed: "Walidacja nie powiodła się",
		},
		user: {
			notSynced:
				"Nie znaleziono użytkownika. Najpierw wywołaj /api/common/auth/sync.",
		},
		notifications: {
			notFound: "Nie znaleziono powiadomienia",
			cursorInvalid: "Nieprawidłowy kursor strony — zacznij od początku",
		},
		property: {
			notFound: "Nie znaleziono nieruchomości",
			forbidden: "Nie masz dostępu do tej nieruchomości",
			transferConfirmationMismatch:
				"Nazwa potwierdzenia nie pasuje. Wpisz dokładną nazwę nieruchomości.",
			transferSelf: "Nie możesz przekazać nieruchomości sobie.",
			transferNotAMember:
				"Wybrany użytkownik nie jest już członkiem tej nieruchomości.",
		},
		projects: {
			notFound: "Nie znaleziono projektu",
			forbidden: "Nie masz uprawnień do tej operacji w tym projekcie",
			constructionRequiresHouse: "Projekt budowy można utworzyć tylko dla domu",
			statusAcceptedOwnerOnly:
				"Tylko właściciel nieruchomości może oznaczyć projekt jako zaakceptowany",
			memberNotEligible: "Nie można dodać tego użytkownika do projektu",
			memberAlreadyAdded: "Ten użytkownik jest już członkiem projektu",
			memberNotFound: "Nie znaleziono członka projektu",
			proposalNotFound: "Nie znaleziono propozycji kosztu",
			proposalAlreadyDecided: "Ta propozycja kosztu została już rozstrzygnięta",
			proposalConflict:
				"Propozycja kosztu właśnie się zmieniła. Odśwież i spróbuj ponownie.",
			proposalsLockedPaid:
				"Projekt jest już opłacony — propozycje kosztów są zablokowane",
			paidRequiresAcceptedCost:
				"Zaakceptuj propozycję kosztu, zanim oznaczysz projekt jako opłacony",
			alreadyPaid: "Projekt jest już oznaczony jako opłacony",
		},
		agentActions: {
			notFound: "Nie znaleziono akcji",
			alreadyResolved: "Ta akcja została już rozstrzygnięta",
			expired: "Ta akcja wygasła",
		},
		inspirations: {
			notFound: "Nie znaleziono inspiracji",
			forbidden: "Nie masz uprawnień do tej operacji",
			unsupportedContentType: "Ten typ pliku nie jest obsługiwany",
			fileTooLarge: "Ten plik jest za duży",
			quotaExceeded: "Ten projekt osiągnął limit inspiracji",
			uploadUrlFailed: "Nie udało się rozpocząć przesyłania. Spróbuj ponownie",
			uploadNotPending: "To przesyłanie nie oczekuje już na potwierdzenie",
			uploadObjectMissing: "Nie znaleziono przesłanego pliku",
			uploadInvalid: "Przesłany plik jest nieprawidłowy",
			storageError: "Magazyn jest chwilowo niedostępny. Spróbuj ponownie",
			noFields: "Brak zmian do zapisania",
		},
		visualizations: {
			notFound: "Nie znaleziono wizualizacji",
			forbidden: "Nie masz uprawnień do tej operacji",
			unsupportedContentType: "Ten typ pliku nie jest obsługiwany",
			fileTooLarge: "Ten plik jest za duży",
			quotaExceeded: "Ten projekt osiągnął limit wizualizacji",
			uploadUrlFailed: "Nie udało się rozpocząć przesyłania. Spróbuj ponownie",
			uploadNotPending: "To przesyłanie nie oczekuje już na potwierdzenie",
			uploadObjectMissing: "Nie znaleziono przesłanego pliku",
			uploadInvalid: "Przesłany plik jest nieprawidłowy",
			storageError: "Magazyn jest chwilowo niedostępny. Spróbuj ponownie",
			noFields: "Brak zmian do zapisania",
		},
		invitation: {
			notFound: "Nie znaleziono zaproszenia",
			alreadyAccepted: "Zaproszenie zostało już zaakceptowane",
			expired: "Zaproszenie wygasło",
			selfInvite: "Nie możesz zaprosić samego siebie — jesteś właścicielem.",
			alreadyMember: "Użytkownik jest już członkiem",
			emailMismatch:
				"To zaproszenie zostało wysłane do {email}. Zaloguj się tym adresem e-mail, aby je zaakceptować.",
			wrongPin:
				"Nieprawidłowy PIN. Poproś zapraszającego o ponowne wyświetlenie kodu.",
			resendQrUnsupported: "Zaproszeń QR nie można wysłać ponownie.",
		},
		membership: {
			alreadyMember: "Jesteś już członkiem",
			notFound: "Nie znaleziono członkostwa",
			cannotRemoveOwner: "Nie można usunąć właściciela nieruchomości",
			cannotChangeOwnerRole: "Nie można zmienić roli właściciela",
		},
		realtime: {
			channelForbidden: "Nie masz dostępu do tego kanału.",
			unsupportedChannel: "Nieobsługiwany kanał.",
		},
		chat: {
			forbidden: "Nie masz dostępu do tego czatu.",
			acknowledgmentInvalid: "Potwierdzenie odczytu jest nieprawidłowe.",
			participantRequired: "Nie jesteś uczestnikiem tego czatu.",
		},
		fcm: {
			tokenNotFound: "Nie znaleziono tokenu powiadomień.",
		},
		journal: {
			cursorInvalid: "Kursor dziennika jest nieprawidłowy lub uszkodzony.",
			limitOutOfRange: "Limit dziennika musi mieścić się w zakresie 1–100.",
		},
		tickets: {
			notFound: "Nie znaleziono zadania",
			forbidden: "Nie masz uprawnień do tej operacji na tym zadaniu",
			projectScopeMismatch: "Ten projekt nie należy do tej nieruchomości",
			topicScopeMismatch: "Ten temat nie należy do tej nieruchomości",
			assigneeNotMember: "Możesz przypisać to tylko członkowi nieruchomości",
			invalidTransition: "Taka zmiana statusu jest niedozwolona",
			cursorInvalid: "Kursor zadań jest nieprawidłowy lub uszkodzony.",
			limitOutOfRange: "Limit zadań musi mieścić się w zakresie 1–100.",
			statusConflict:
				"Zadanie właśnie się zmieniło. Odśwież i spróbuj ponownie.",
		},
		ticketComments: {
			notFound: "Nie znaleziono komentarza",
			forbidden: "Nie masz uprawnień do usunięcia tego komentarza",
		},
		ticketAttachments: {
			notFound: "Nie znaleziono załącznika",
			forbidden: "Nie masz uprawnień do usunięcia tego załącznika",
			unsupportedContentType: "Ten typ pliku nie jest obsługiwany",
			fileTooLarge: "Ten plik jest za duży",
			validationFailed: "Przesłany plik jest nieprawidłowy",
		},
		mediaUploads: {
			notFound: "Nie znaleziono przesłanego obrazu",
			forbidden: "Nie masz uprawnień do użycia tego przesłanego obrazu",
			propertyRequired: "Nieruchomość jest wymagana dla tego przesłania",
			unsupportedContentType: "Ten typ obrazu nie jest obsługiwany",
			fileTooLarge: "Ten obraz jest za duży",
			validationFailed: "Przesłany obraz jest nieprawidłowy",
			objectMissing: "Nie znaleziono przesłanego obrazu",
		},
		documents: {
			notFound: "Nie znaleziono dokumentu",
			forbidden: "Nie masz uprawnień do tej operacji",
			unsupportedContentType: "Ten typ pliku nie jest obsługiwany",
			fileTooLarge: "Ten plik jest za duży",
			validationFailed: "Przesłany plik jest nieprawidłowy",
			granteeNotMember: "Możesz to udostępnić tylko członkowi nieruchomości",
			objectMissing: "Nie znaleziono przesłanego pliku",
		},
		topics: {
			notFound: "Nie znaleziono tematu",
			forbidden: "Tylko menedżerowie nieruchomości mogą zarządzać tematami",
			projectScopeMismatch: "Ten projekt nie należy do tej nieruchomości",
		},
		budgetEntries: {
			notFound: "Nie znaleziono wpisu budżetowego",
			forbidden: "Tylko właściciel nieruchomości może zarządzać budżetem",
			projectScopeMismatch: "Ten projekt nie należy do tej nieruchomości",
			topicScopeMismatch: "Ten temat nie należy do tej nieruchomości",
			amountRequired: "Kwota jest wymagana",
			amountInvalid: "Ta kwota jest nieprawidłowa",
			invalidStatus: "Ta zmiana statusu jest niedozwolona",
			statusConflict:
				"Wpis budżetowy właśnie się zmienił. Odśwież i spróbuj ponownie.",
			noFields: "Brak zmian do zapisania",
			cursorInvalid: "Kursor budżetu jest nieprawidłowy lub uszkodzony.",
			limitOutOfRange: "Limit budżetu musi mieścić się w zakresie od 1 do 100.",
			unsupportedContentType: "Ten typ pliku nie jest obsługiwany",
			fileTooLarge: "Ten plik jest za duży",
			uploadUrlFailed:
				"Nie udało się przygotować przesyłania. Spróbuj ponownie.",
			objectMissing: "Brak przesłanego pliku. Spróbuj przesłać ponownie.",
			validationFailed: "Przesłany plik nie przeszedł weryfikacji",
			storageError: "Błąd magazynu. Spróbuj ponownie.",
			documentNotReady: "Dokument nie jest jeszcze gotowy",
		},
	},

	apiPushCopy: {
		chat: {
			titlePrivate: "{property} · Mareczek",
			titleShared: "{property}",
			bodyPreviewFallback: "(pusta wiadomość)",
		},
		journal: {
			memberInvited: "{actor} zaprosił(a) nowego członka ({role})",
			memberJoined: "{actor} dołączył(a) do nieruchomości",
			propertyCreated: "{actor} utworzył(a) nieruchomość",
			projectCreated: "{actor} utworzył(a) projekt {project}",
			projectStatusChanged:
				"{actor} zmienił(a) status {project}: {from} → {to}",
			projectCostProposed:
				"{actor} zaproponował(a) koszt {amount} {currency} w {project}",
			projectCostAccepted:
				"{actor} zaakceptował(a) koszt {amount} {currency} w {project}",
			projectCostRejected:
				"{actor} odrzucił(a) koszt {amount} {currency} w {project}",
			projectPaid:
				"{actor} oznaczył(a) {amount} {currency} jako opłacone w {project}",
			projectMemberAdded: "{actor} dodał(a) {member} do {project}",
			projectMemberRemoved: "{actor} usunął/usunęła {member} z {project}",
			projectArchived: "{actor} zarchiwizował(a) {project}",
			projectUnarchived: "{actor} przywrócił(a) {project}",
			inspirationCreated: "{actor} dodał(a) inspirację do {project}",
			inspirationDeleted: "{actor} usunął(usunęła) inspirację z {project}",
			visualizationCreated: "{actor} dodał(a) wizualizację do {project}",
			visualizationDeleted: "{actor} usunął(usunęła) wizualizację z {project}",
			documentCreated: "{actor} dodał(a) dokument",
			documentDeleted: "{actor} usunął(usunęła) dokument",
			documentUpdated: "{actor} zaktualizował(a) dokument",
		},
		property: {
			joinApproved: "Dodano Cię do {property}.",
			joinRequest: "{requester} prosi o dołączenie do nieruchomości",
		},
		ticket: {
			created: "{actor} dodał(a) zadanie: {title}",
			createdDefect: "{actor} zgłosił(a) usterkę: {title}",
			assigned: "{actor} przypisał(a) Ci: {title}",
			statusChanged: "{title}: {status}",
			statusDone: "{actor} ukończył(a): {title}",
			statusDoneDefect: "{actor} naprawił(a) usterkę: {title}",
			reminder: "Przypomnienie: {title}",
			reminderDefect: "Przypomnienie o usterce: {title}",
			commentMention: "{actor} wspomniał(a) o Tobie w komentarzu: {title}",
			commentAdded: "{actor} skomentował(a): {title}",
			mention: "{actor} wspomniał(a) o Tobie w zgłoszeniu: {title}",
		},
	},

	sidebar: {
		navLabel: "Główna nawigacja",
		properties: "Nieruchomości",
		myTickets: "Moje zadania",
		profile: "Profil",
		settings: "Ustawienia",
		signOut: "Wyloguj się",
		more: "Więcej",
		collapseMenu: "Zwiń menu",
		expandMenu: "Rozwiń menu",
	},

	settings: {
		title: "Ustawienia",
		notifications: {
			title: "Powiadomienia",
			masterToggle: "Zezwalaj na powiadomienia",
			quietHoursToggle: "Cisza nocna",
			quietHoursStart: "Od",
			quietHoursEnd: "Do",
			propertiesTitle: "Wyciszone nieruchomości",
			muteProperty: "Wycisz {property}",
			save: "Zapisz powiadomienia",
			savedTimeZone: "Zapisana strefa czasu",
			loading: "Ładowanie powiadomień",
			error: "Nie udało się zapisać ustawień powiadomień.",
		},
	},

	theme: {
		light: "jasny",
		dark: "ciemny",
		auto: "auto",
		toggleLabel: "Motyw",
	},

	profile: {
		title: "Profil",
		firstNameLabel: "Imię",
		firstNamePlaceholder: "Jan",
		lastNameLabel: "Nazwisko",
		lastNamePlaceholder: "Kowalski",
		languageLabel: "Język",
		aiContextLabel: "Kontekst dla AI",
		aiContextPlaceholder:
			"Krótki opis o sobie, który będzie przekazywany do AI: kim jesteś, w jakich jednostkach pracujesz, w jakim języku oczekujesz odpowiedzi.",
		mainRoleLabel: "Główna rola",
		save: "Zapisz",
		cancel: "Anuluj",
		saved: "Zapisano",
		deleteAccount: {
			sectionTitle: "Usuń konto",
			sectionBody:
				"Usuwa konto, dane osobowe, dostęp i nieruchomości, których jesteś właścicielem. Wiadomości czatu zostają w historii jako Usunięty użytkownik.",
			open: "Usuń konto",
			dialogTitle: "Usunąć konto?",
			dialogBody:
				"Konto, dane osobowe, Twoje nieruchomości, zaproszenia i członkostwa zostaną usunięte. Wiadomości czatu pozostaną widoczne jako Usunięty użytkownik. Tej operacji nie można cofnąć.",
			confirmationPhrase: "USUŃ",
			phraseLabel: "Wpisz {phrase}, aby potwierdzić",
			confirm: "Usuń konto",
			submitting: "Usuwanie...",
			cancel: "Anuluj",
			error: "Nie udało się usunąć konta.",
		},
		errors: {
			firstNameRequired: "Imię jest wymagane.",
			firstNameTooLong: "Maksymalna długość: {max} znaków.",
			lastNameRequired: "Nazwisko jest wymagane.",
			lastNameTooLong: "Maksymalna długość: {max} znaków.",
			aiContextTooLong: "Maksymalna długość: {max} znaków.",
			mainRoleRequired: "Wybierz rolę.",
			unknown: "Nie udało się zapisać profilu.",
		},
	},

	onboarding: {
		welcomeHeading: "Witaj w zPlanem!",
		justOneMoreThing: "Jeszcze jedna rzecz",
		subtitle:
			"Powiedz nam o sobie, abyśmy mogli dostosować Twoje doświadczenie.",
		firstNameLabel: "Imię",
		firstNamePlaceholder: "Jan",
		lastNameLabel: "Nazwisko",
		lastNamePlaceholder: "Kowalski",
		languageLabel: "Język",
		mainRoleLabel: "Jestem…",
		mainRolePlaceholder: "Wybierz swoją rolę",
		termsLabel: "Akceptuję {link}",
		termsLink: "Regulamin",
		submit: "Zaczynamy",
		errors: {
			firstNameRequired: "Imię jest wymagane.",
			firstNameTooLong: "Maksymalna długość: {max} znaków.",
			lastNameRequired: "Nazwisko jest wymagane.",
			lastNameTooLong: "Maksymalna długość: {max} znaków.",
			mainRoleRequired: "Wybierz rolę.",
			termsRequired: "Musisz zaakceptować Regulamin.",
			unknown: "Nie udało się dokończyć rejestracji.",
		},
	},
}
